import { useCallback, useEffect, useState } from 'react'
import { Plus } from '@phosphor-icons/react'

// Hooks
import { useModal, useDebounce, useTasks, useProject } from 'Hooks/'

// Components
import { ButtonsModal } from 'Organisms'

// Const
import SelectWithHint from 'Organisms/SelectWithHint'
import LoadingButton from 'Organisms/LoadingButton'
import TableTasks from './TableTasks'
import { formatAddTaskToProjectAdapter } from 'adapters/project'
import { Loading } from 'Organisms/Loading'

const ModalAddTaskToProject = ({ visible, setVisible, oldTask, idProject, refreshProjectInfo }) => {
  const { Modal, hide } = useModal({ identifier: 'addtasktoproject', visible, setVisible })
  const { listTasksHook, loadingTaskHook, getAllTasksHook, createTaskHook } = useTasks()
  const { loadingProjectHook, addTaskToProjectHook } = useProject()

  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [listSelectedTasks, setListSelectedTask] = useState([])
  const [errors, setErrors] = useState({})
  const [newTask, setNewTask] = useState('')
  const [showDropdown, setShowDropdown] = useState(true)

  useEffect(() => {
    ;(async () => {
      const res = await getAllTasksHook()
      setShowDropdown(res.length > 0)
    })()
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const listTaskCreate = formatAddTaskToProjectAdapter({ listTask: listSelectedTasks })
      await addTaskToProjectHook({ idProject, listIdTask: listTaskCreate })
      await refreshProjectInfo()
    } finally {
      setLoading(false)
      hide()
    }
  }

  const filterDebounce = useCallback(
    useDebounce(({ search }) => {
      getAllTasksHook({ search })
    }, 1000),
    []
  )

  const formatSelect = ({ list }) =>
    list.map((item) => {
      return {
        value: item.id,
        label: item?.name
      }
    })

  const hanldeChangeSearch = (searchParam) => {
    setSearch(searchParam)
    filterDebounce({ search: searchParam })
  }

  const handleChangeSelectTask = (value) => {
    const task = listTasksHook.find((item) => item.id === value.value)

    if ([...listSelectedTasks, ...oldTask.map((task) => task.task)].some((taskS) => taskS.id === task.id)) return

    setListSelectedTask((prevState) => [...prevState, task])
  }

  const handleCreateTask = async () => {
    try {
      if (!newTask) return setErrors({ task: true })
      setLoading(true)
      const task = await createTaskHook({ taskName: newTask })
      setListSelectedTask([...listSelectedTasks, task])
      setNewTask('')
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal>
      <>
        {loadingProjectHook && <Loading />}

        <div className="modal-content" style={{ maxWidth: 700 }}>
          <div className="modal-header">
            <h3 className="modal-title fs-5" id="exampleModalLabel">
              Add a task to the project
            </h3>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col">
                <div className="row justify-content-center">
                  <form id="addTaskToProject" className="col-10 d-grid" onSubmit={onSubmit}>

                    { showDropdown && (
                      <div className="row justify-content-between align-items-end mb-3 gy-md-0 gy-4">
                        <div className="col-md-6 col-12">
                          <label className='form-label'>Select a task</label>
                          <SelectWithHint
                            loading={loadingTaskHook}
                            value={search}
                            id="selectTask"
                            placeholder="Select a task"
                            formatSelect={formatSelect({ list: listTasksHook })}
                            handleInputChange={hanldeChangeSearch}
                            handleChange={handleChangeSelectTask}
                            noOptionsMessage="Task not found"
                            loadingMessage="Search"
                          />
                        </div>
                        <div className="col-md-auto col-12">
                          <LoadingButton
                            type="button"
                            onClick={() => setShowDropdown(false)}
                            className="btn btn-outline-primary"
                            loading={loading}>
                              <Plus color='#00DDD8'/>
                          </LoadingButton>
                        </div>
                      </div>
                    )}

                    { !showDropdown && (
                      <div className="row align-items-end mb-3">
                        <div className="col-md-8 col-12">
                          <label className='form-label'>Create a new task</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.task ? 'is-invalid' : ''}`}
                            id="inputTask"
                            placeholder="Enter task name"
                            onChange={(e) => {
                              if (errors?.task) setErrors({ ...errors, task: false })
                              setNewTask(e.target.value)
                            }}
                            value={newTask}
                          />
                          <div className="invalid-feedback">Please write new task.</div>
                        </div>

                        <div className="col-md-auto col-12">
                          <div className="row justify-content-end">
                            <div className="col-6 pe-0">
                              <LoadingButton
                                type="button"
                                onClick={handleCreateTask}
                                className="btn btn-primary btn-sm me-2"
                                id="createNewTask"
                                loading={loading}>
                                Create
                              </LoadingButton>
                            </div>
                            <div className="col-6 ps-0">
                              <LoadingButton
                                type="button"
                                onClick={() => setShowDropdown(true)}
                                className="btn btn-outline-secondary btn-sm ml-2"
                                loading={loading}>
                                Dismiss
                              </LoadingButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    { listSelectedTasks.length === 0 &&
                      <div className='card card-secondary py-5'>
                        <h5 className='text-center pb-0'>Add or create a new task</h5>
                        <p className='text-center pt-0'>Choose a task from the list</p>
                      </div>
                    }

                    {listSelectedTasks.length > 0 && (
                      <div className="card card-secondary">
                      <table className="table">
                        <thead>
                          <tr>
                            <th scope="col">Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listSelectedTasks.map((task) => (
                            <tr key={task.id}>
                              <td>
                                <span className="row justify-content-between mb-2">
                                  <span className="col-auto">{task?.name}</span>
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )}

                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <div className="row justify-content-end">
              <div className='col-auto'>
                <LoadingButton type="button" className="btn btn-secondary" onClick={hide}>
                  Cancel
                </LoadingButton>
              </div>
              <div className='col-auto'>
                <LoadingButton type="submit" form="addTaskToProject" className="btn btn-primary" disabled={loading}>
                  Add Task
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default ModalAddTaskToProject
